import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DataFallback from '../feedback/DataFallback';
import {
  getShopifyVendorConfig,
  listVendors,
  getShopifyStoreProducts,
  pushProductToCatalog,
  bulkPushProductsToCatalog,
  unmapProduct,
  bulkUnmapProducts,
  updateProductMapping,
  approveProduct,
  getCurrentUserRole,
  getSyncFieldFilters,
  getInternalStoreProducts,
  linkExistingProduct
} from '../../services/shipturtleApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showToast } from '../../utils/toast';
import { FaSearch, FaLink, FaPlus } from 'react-icons/fa';
import './VendorStoreManager.css';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';

const getProductId = (product) => {

  const id = product.vendor_shopify_product_id;
  return id != null ? String(id) : null;
};

const formatTags = (tags) => {
  if (!tags) return '-';
  if (Array.isArray(tags)) return tags.join(', ');
  if (typeof tags === 'string') {
    // Replace pipe or space-separated tags if they exist, but standard Shopify is comma
    return tags.split(/[,|]/).map(t => t.trim()).filter(Boolean).join(', ');
  }
  return String(tags);
};

const DEFAULT_PRODUCT_FILTERS = {
  vendor_shopify_status: '',
  suspire_shopify_status: ''
};

// Minimum characters before filtering; below this the dropdown just browses.
const VENDOR_SEARCH_MIN_CHARS = 2;
// How many vendors to show before the user starts typing.
const VENDOR_INITIAL_LIST_SIZE = 20;

const getVendorName = (vendor) => vendor.display_name || vendor.company_name || '';

// Wrap the matched substring in <mark> so the hit is visible while typing.
const highlightMatch = (text, search) => {
  const query = (search || '').trim();
  if (!text || query.length < VENDOR_SEARCH_MIN_CHARS) return text;

  // Escape regex metacharacters — vendor names contain '&', '(', '.' etc.
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));

  return parts.map((part, idx) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={idx}>{part}</mark>
      : part
  );
};

const StatusBadge = ({ status, showCallout = false }) => {
  const s = (status || '').toUpperCase();
  const styles = {
    ACTIVE: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: 'Active' },
    DRAFT: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: 'Draft' },
    ARCHIVED: { bg: '#ffedd5', color: '#c2410c', border: '#fed7aa', label: 'Archived' },
    UNLISTED: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: 'Unlisted' },
    DELETED: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', label: 'Deleted' },
  };

  const style = styles[s];
  if (!style) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span className="badge" style={{
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
        textTransform: 'capitalize'
      }}>
        {style.label}
      </span>
      {showCallout && s === 'DELETED' && (
        <span style={{
          color: '#dc2626',
          fontSize: '10px',
          fontWeight: '800',
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap'
        }}>
          Removed from Suspire Shopify
        </span>
      )}
    </div>
  );
};

const VendorStoreManager = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    price: '',
    sku: '',
    status: '',
    variantsData: []
  });
  const [modalError, setModalError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [unsyncedVendors, setUnsyncedVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(searchParams.get('vendor_id') || null);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [mappedCount, setMappedCount] = useState(0);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('unmapped'); // 'unmapped' | 'mapped'
  const [vendorSearch, setVendorSearch] = useState('');
  const [unsyncedVendorSearch, setUnsyncedVendorSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unsyncedDropdownOpen, setUnsyncedDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const unsyncedDropdownRef = useRef(null);

  const [linkModalProduct, setLinkModalProduct] = useState(null);
  useTaraTerminalFocus('vendor_store', {
    vendor_id: selectedVendorId || undefined,
    showing: activeTab === 'mapped' ? 'mapped products' : 'unmapped products',
    counts: { total: totalCount, mapped: mappedCount, unmapped: unmappedCount },
    linking_product: linkModalProduct?.title || undefined,
  });
  const [merchantSearchQuery, setMerchantSearchQuery] = useState('');
  const [merchantProducts, setMerchantProducts] = useState([]);
  const [selectedMerchantProduct, setSelectedMerchantProduct] = useState(null);
  const [linkVariantMappings, setLinkVariantMappings] = useState({});
  const [merchantProductsLoading, setMerchantProductsLoading] = useState(false);
  const [merchantDropdownOpen, setMerchantDropdownOpen] = useState(false);
  const merchantDropdownRef = useRef(null);
  const merchantSearchTimer = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [confirmModal, setConfirmModal] = useState({ show: false, props: {} });
  
  // Filter state
  const [filters, setFilters] = useState(DEFAULT_PRODUCT_FILTERS);
  const [filterLoading, setFilterLoading] = useState(false);

  // Step 1: Initial call on mount
  const fetchProducts = useCallback(async (vendorId, currentFilters = DEFAULT_PRODUCT_FILTERS) => {
    if (!vendorId) return;
    try {
      setProductsLoading(true);
      const res = await getShopifyStoreProducts(vendorId, currentFilters);
      setProducts(res.products || []);
      setMappedCount(res.total_mapped || res.mapped_count || 0);
      setUnmappedCount(res.total_unmapped || res.unmapped_count || 0);
      setTotalCount(res.total || res.products?.length || 0);

      if (res.message && (!res.products || res.products.length === 0)) {
        console.log('Backend message:', res.message);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      showToast({ type: 'error', message: 'Failed to load products from store.' });
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // The vendors endpoint paginates at 20 per page by default, so walk every
  // page to load the complete list instead of silently stopping at page 1.
  const fetchAllVendors = useCallback(async (params) => {
    const all = [];
    let page = 1;
    let hasNext = true;

    while (hasNext) {
      const res = await listVendors({ ...params, page, page_size: 100 });
      const batch = res.results || res.vendors || [];
      all.push(...batch);

      // Fall back to the batch size when the API omits pagination metadata,
      // so a non-paginated response can't spin this loop forever.
      hasNext = res.has_next === true && batch.length > 0;
      page += 1;
    }

    return all;
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const [synced, unsynced] = await Promise.all([
        fetchAllVendors({ shopify_connected: true }),
        fetchAllVendors({ shopify_connected: false })
      ]);
      setVendors(synced);
      setUnsyncedVendors(unsynced);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      showToast({ type: 'error', message: 'Failed to load vendors list.' });
    } finally {
      setVendorsLoading(false);
    }
  }, [fetchAllVendors]);

  const fetchInitialConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Call BOTH APIs in parallel: shopify-config AND user role
      const [shopifyRes, roleData] = await Promise.all([
        getShopifyVendorConfig(),
        getCurrentUserRole().catch(() => ({ role: null }))
      ]);

      console.log('[VendorStore] shopify-config response:', shopifyRes);
      console.log('[VendorStore] user role:', roleData?.role);

      if (shopifyRes.error) {
        setError(shopifyRes.error);
        return;
      }

      // Determine admin status: check BOTH the shopify-config API AND the user role API
      const adminFromConfig = shopifyRes.is_admin === true;
      const adminFromRole = roleData?.role === 'admin';
      const userIsAdmin = adminFromConfig || adminFromRole;

      console.log('[VendorStore] Admin detection:', { adminFromConfig, adminFromRole, userIsAdmin });

      // Set config with the correct is_admin flag
      const finalConfig = { ...shopifyRes, is_admin: userIsAdmin };
      setConfig(finalConfig);

      if (userIsAdmin) {
        // Admin mode: show vendor dropdown
        fetchVendors();

        // If we have a vendor_id in the URL, fetch its products
        const initialVendorId = searchParams.get('vendor_id');
        if (initialVendorId) {
          fetchProducts(initialVendorId, filters);
        }
      } else if (shopifyRes.vendor_id) {
        // Vendor/Brand mode: auto-load their own products
        setSelectedVendorId(shopifyRes.vendor_id);
        fetchProducts(shopifyRes.vendor_id, filters);
      }
    } catch (err) {
      console.error('Error fetching initial config:', err);
      setError('Could not establish connection to vendor configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchVendors, fetchProducts, searchParams]);

  useEffect(() => {
    fetchInitialConfig();
  }, [fetchInitialConfig]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (unsyncedDropdownRef.current && !unsyncedDropdownRef.current.contains(e.target)) {
        setUnsyncedDropdownOpen(false);
      }
      if (merchantDropdownRef.current && !merchantDropdownRef.current.contains(e.target)) {
        setMerchantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenLinkPopup = async (product) => {
    setLinkModalProduct(product);
    setMerchantSearchQuery('');
    setMerchantProducts([]);
    setSelectedMerchantProduct(null);
    setLinkVariantMappings({});
    setMerchantDropdownOpen(false);
    setModalError(null);
    

    const wasPreviouslyMapped = product.internal_shopify_id && 
                                 product.internal_shopify_id !== '' &&
                                 product.mapping_status === 'unmapped';
    
    if (wasPreviouslyMapped && selectedVendorId) {
      try {
        setMerchantProductsLoading(true);
        
        const res = await getInternalStoreProducts(selectedVendorId, '', product.internal_shopify_id);
        const internalProducts = res.results || [];
        
        const matchedProduct = internalProducts.length > 0 ? internalProducts[0] : null;
        
        if (matchedProduct) {
          setSelectedMerchantProduct(matchedProduct);
          setMerchantSearchQuery(matchedProduct.title);
          setMerchantProducts([matchedProduct]); 
          
          if (matchedProduct.variants && product.variants) {
            const variantMappings = {};
            matchedProduct.variants.forEach(merchantVar => {
              const merchantVarId = String(merchantVar.id || merchantVar.internal_variant_id);
              const matchingVendorVariant = product.variants.find(vv => 
                (vv.sku && vv.sku === merchantVar.sku) ||
                (vv.title && vv.title === (merchantVar.variant_name || merchantVar.title))
              );
              if (matchingVendorVariant) {
                variantMappings[merchantVarId] = String(matchingVendorVariant.vendor_shopify_variant_id);
              }
            });
            setLinkVariantMappings(variantMappings);
          }
        } else {
          fetchMerchantProducts('', false);
        }
      } catch (err) {
        console.error('Error auto-fetching product by internal_shopify_id:', err);
        fetchMerchantProducts('', false);
      } finally {
        setMerchantProductsLoading(false);
      }
    } else {
      fetchMerchantProducts('', false); 
    }
  };

  const handleCloseLinkPopup = () => {
    setLinkModalProduct(null);
    setModalError(null);
  };

  const fetchMerchantProducts = async (q = '', open = true) => {
    setMerchantProductsLoading(true);
    if (open) setMerchantDropdownOpen(true);
    try {
      const res = await getInternalStoreProducts(selectedVendorId, q);
      // API returns { results: [...], count: N }
      setMerchantProducts(res.results || []);
    } catch (err) {
      console.error('Error fetching merchant products:', err);
      setMerchantProducts([]);
    } finally {
      setMerchantProductsLoading(false);
    }
  };

  const handleMerchantSearchChange = (q) => {
    setMerchantSearchQuery(q);
    if (merchantSearchTimer.current) {
      clearTimeout(merchantSearchTimer.current);
    }
    merchantSearchTimer.current = setTimeout(() => {
      fetchMerchantProducts(q);
    }, 300);
  };

  const selectMerchantProduct = (product) => {
    setSelectedMerchantProduct(product);
    setMerchantDropdownOpen(false);
    setMerchantSearchQuery(product.title);
    setLinkVariantMappings({});
  };

  const handlePushToMap = async () => {
    if (!linkModalProduct || !selectedMerchantProduct) return;
    
    const variant_mappings = [];
    Object.entries(linkVariantMappings).forEach(([merchantVarId, vendorVarId]) => {
      if (vendorVarId && vendorVarId !== '') { 
        variant_mappings.push({
          internal_variant_id: String(merchantVarId),
          vendor_shopify_variant_id: Number(vendorVarId)
        });
      }
    });

    const payload = {
      vendor_shopify_product_id: getProductId(linkModalProduct),
      internal_product_id: String(selectedMerchantProduct.id || selectedMerchantProduct.internal_product_id),
      variant_mappings
    };
    
    try {
      setSubmittingId(`link_${getProductId(linkModalProduct)}`);
      setModalError(null);
      const response = await linkExistingProduct(selectedVendorId, payload);
      
      // Show success message from backend response
      if (response && response.message) {
        showToast({ type: 'success', message: response.message });
      } else if (response && response.was_remapped) {
        showToast({ type: 'success', message: 'Product re-linked successfully.' });
      } else if (response && response.already_existed) {
        showToast({ type: 'info', message: 'Product already mapped.' });
      } else {
        showToast({ type: 'success', message: 'Product linked successfully!' });
      }
      
      handleCloseLinkPopup();
      await fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error linking product:', err);
      setModalError(err.message || 'Failed to link product.');
    } finally {
      setSubmittingId(null);
    }
  };

  // Re-map a product that is still linked but has sync switched off.
  //
  // Unmapping preserves the link (external product + variant ids), so this needs
  // no variant matching at all — the backend recognises the already-linked id and
  // flips the flag back. That's why the link modal is skipped entirely here.
  const handleRemap = async (product) => {
    if (!selectedVendorId) return;

    const productId = getProductId(product);
    const internalProductId = product.internal_product_id || product.terminal_product_id;
    if (!internalProductId) {
      showToast({
        type: 'error',
        message: 'Cannot re-map: this product is not linked to a merchant product.',
      });
      return;
    }

    try {
      setSubmittingId(productId);
      const response = await linkExistingProduct(selectedVendorId, {
        vendor_shopify_product_id: productId,
        internal_product_id: String(internalProductId),
        variant_mappings: [],
      });
      showToast({
        type: 'success',
        message:
          response?.message || 'Product re-mapped. Vendor updates will sync again.',
      });
      await fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error re-mapping product:', err);
      showToast({ type: 'error', message: err.message || 'Failed to re-map product.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const selectVendor = (vendorId) => {
    setSelectedVendorId(vendorId);
    setDropdownOpen(false);
    setUnsyncedDropdownOpen(false);
    setCurrentPage(1);
    // Reset filters when changing vendors
    setFilters(DEFAULT_PRODUCT_FILTERS);
    if (vendorId) {
      const vendor = vendors.find(v => v.id === vendorId);
      setVendorSearch(vendor?.display_name || vendor?.company_name || '');
      setSearchParams({ vendor_id: vendorId });
      fetchProducts(vendorId, DEFAULT_PRODUCT_FILTERS);
      setUnsyncedVendorSearch('');
    } else {
      setVendorSearch('');
      setProducts([]);
      setTotalCount(0);
      setMappedCount(0);
      setUnmappedCount(0);
      setSearchParams({});
    }
  };

  const selectUnsyncedVendor = (vendorId) => {
    if (!vendorId) return;
    navigate(`/suspire-terminal/vendors?vendor_id=${vendorId}&tab=connect-shopify`);
  };

  // Search only kicks in at 2+ characters; below that the dropdown shows an
  // initial slice of the list so it's still browsable without typing.
  const searchVendorList = (list, search) => {
    const q = search.trim().toLowerCase();
    if (q.length < VENDOR_SEARCH_MIN_CHARS) {
      return { items: list.slice(0, VENDOR_INITIAL_LIST_SIZE), isSearching: false };
    }

    const matched = list.filter(v =>
      (v.display_name?.toLowerCase().includes(q)) ||
      (v.company_name?.toLowerCase().includes(q))
    );

    // Surface names that start with the query before mid-word matches.
    const sorted = [...matched].sort((a, b) => {
      const aName = getVendorName(a).toLowerCase();
      const bName = getVendorName(b).toLowerCase();
      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return aName.localeCompare(bName);
    });

    return { items: sorted, isSearching: true };
  };

  const filteredVendors = useMemo(
    () => searchVendorList(vendors, vendorSearch),
    [vendors, vendorSearch]
  );

  const filteredUnsyncedVendors = useMemo(
    () => searchVendorList(unsyncedVendors, unsyncedVendorSearch),
    [unsyncedVendors, unsyncedVendorSearch]
  );

  const handlePushToCatalog = async (product) => {
    const productId = getProductId(product);
    if (!selectedVendorId || !productId) {
      console.error('[VendorStore] Cannot push: missing product ID.', {
        product,
        availableKeys: Object.keys(product)
      });
      showToast({ type: 'error', message: 'Product ID is missing in the data from Shopify. Check console for details.' });
      return;
    }

    try {
      setSubmittingId(productId);
      await executePush(productId);
    } catch (err) {
      console.error('Error in push:', err);
      showToast({ type: 'error', message: err.message || 'Failed to push product.' });
      setSubmittingId(null);
    }
  };

  const executePush = async (productId) => {
    try {
      // v2 ingest fetches the full product server-side from the vendor store,
      // so only the vendor Shopify product id is needed. Product/variant/media
      // details are read from the store at push time (store = source of truth).
      const payload = {
        vendor_shopify_product_id: Number(productId),
      };

      const response = await pushProductToCatalog(selectedVendorId, payload);

      // Check for application-level errors despite 200 OK
      if (response && (response.error || response.status === 'error' || response.success === false)) {
        throw new Error(response.error || response.message || 'Backend failed to push product.');
      }

      if (response && response.already_existed) {
        showToast({
          type: 'info',
          message: 'Already pushed'
        });
        return;
      }

      const mappingStatus = response?.mapping_status;

      if (mappingStatus === 'mapped') {
        showToast({
          type: 'success',
          message: 'Pushed to Suspire Shopify'
        });
      } else if (mappingStatus === 'pending') {
        showToast({
          type: 'info',
          message: '⏳ Awaiting admin approval'
        });
      } else if (mappingStatus === 'fail_to_sync' || response?.shopify_synced === false) {
        showToast({
          type: 'warning',
          message: 'Saved but Shopify sync delayed, refresh in a moment'
        });
      } else {
        showToast({
          type: 'success',
          message: 'Product pushed and live'
        });
      }

      // Always re-fetch the full list to get live DB + Shopify state
      await fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error pushing product:', err);
      showToast({ type: 'error', message: err.message || 'Failed to push product.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleApprove = async (product) => {
    const productId = product.internal_product_id;
    if (!productId) return;

    try {
      setSubmittingId(productId);
      await approveProduct(productId, { mode: 'push' });
      showToast({ type: 'success', message: 'Product approved and pushed to Map!' });
      fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error approving product:', err);
      showToast({ type: 'error', message: err.message || 'Failed to approve product.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleStartEdit = (product) => {
    setEditingProduct(product);
    setModalError(null);

    // Status normalization
    const currentStatus = product.vendor_shopify_status || 'DRAFT';
    const statusValue = ['ACTIVE', 'DRAFT', 'ARCHIVED'].includes(currentStatus.toUpperCase())
      ? currentStatus.toUpperCase()
      : 'DRAFT';

    setEditForm({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      sku: product.sku || '',
      status: statusValue,
      variantsData: (product.variants || []).map(v => ({
        id: v.internal_variant_id,
        title: v.title,
        price: v.price || '',
        compare_price: v.compare_price || '',
        sku: v.sku || '',
        hs_code: v.hs_code || '',
        length_cm: v.length_cm || '',
        width_cm: v.width_cm || '',
        height_cm: v.height_cm || '',
        commission_pct: v.commission_pct || '',
        gst_rate: v.gst_rate || ''
      }))
    });
    setIsEditing(true);
  };

  const handleUpdateMapping = async () => {
    if (!editingProduct || !editingProduct.mapping_id) return;
    setModalError(null);

    // Validation: compare_price must be >= price
    // Note: prompt says price applies to ALL variants on save.
    // We check all rows in the form.
    for (let i = 0; i < editForm.variantsData.length; i++) {
      const v = editForm.variantsData[i];
      if (v.compare_price && Number(v.compare_price) > 0) {
        if (Number(v.compare_price) < Number(v.price)) {
          setModalError(`Validation Error: Variant "${v.title}" compare price cannot be less than price.`);
          return;
        }
      }
    }

    try {
      setSubmittingId(editingProduct.mapping_id);

      // Calculate Payload - follow exact structure
      const payload = {};

      // Product level
      payload.title = editForm.title;
      payload.description = editForm.description;
      if (config.is_admin) {
        payload.status = editForm.status;
      }

      // Admin-only shipping & tax fields removed from product level — now per-variant

      // Variants section (includes per-variant dimensions, commission, gst)
      payload.variants = editForm.variantsData.map(v => ({
        id: v.id,
        price: String(v.price),
        compare_price: v.compare_price === '' ? "" : String(v.compare_price),
        sku: v.sku,
        hs_code: v.hs_code,
        length_cm: v.length_cm !== '' ? v.length_cm : undefined,
        width_cm: v.width_cm !== '' ? v.width_cm : undefined,
        height_cm: v.height_cm !== '' ? v.height_cm : undefined,
        commission_pct: v.commission_pct !== '' ? v.commission_pct : undefined,
        gst_rate: v.gst_rate !== '' ? v.gst_rate : undefined
      }));

      if (Object.keys(payload).length === 0) {
        showToast({ type: 'info', message: 'No changes detected.' });
        setIsEditing(false);
        return;
      }

      const response = await updateProductMapping(selectedVendorId, editingProduct.mapping_id, payload);

      showToast({ type: 'success', message: 'Product updated successfully' });

      // Sync warnings
      if (response.vendor_shopify_synced === false) {
        showToast({ type: 'warning', message: 'Saved but failed to sync to vendor\'s Shopify store' });
      }
      if (response.suspire_shopify_synced === false) {
        showToast({ type: 'warning', message: 'Saved but failed to sync to Suspire Shopify' });
      }

      setIsEditing(false);
      setEditingProduct(null);
      fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error updating product:', err);
      setModalError(err.message || 'Failed to update product. Please check the fields and try again.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleUnmap = async (product) => {
    if (!selectedVendorId || !product.mapping_id) return;

    if (!window.confirm('Unmap this product? Vendor updates will stop syncing and it moves back to the unmapped list. The link is kept, so you can re-map it later without matching variants again.')) {
      return;
    }

    try {
      const productId = getProductId(product);
      setSubmittingId(productId);
      await unmapProduct(selectedVendorId, { mapping_id: product.mapping_id });
      showToast({ type: 'success', message: 'Product unmapped. Vendor updates will no longer sync.' });

      // Full re-fetch to get live Shopify status (local state would keep stale values)
      await fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error unmapping product:', err);
      showToast({ type: 'error', message: err.message || 'Failed to unmap product.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleBulkUnmap = async () => {
    if (selectedProducts.size === 0 || !selectedVendorId) return;

    if (!window.confirm(`Are you sure you want to unmap ${selectedProducts.size} selected products?`)) {
      return;
    }

    try {
      setSubmittingId('bulk-unmap');

      const mappingIds = Array.from(selectedProducts);
      const response = await bulkUnmapProducts(selectedVendorId, { mapping_ids: mappingIds });

      if (response.success) {
        showToast({
          type: 'success',
          message: response.message || `${response.unmapped_count} products unmapped. Vendor updates will no longer sync.`
        });
      } else {
        showToast({ type: 'warning', message: response.message || 'Bulk unmap completed with some issues.' });
      }

      setSelectedProducts(new Set());
      await fetchProducts(selectedVendorId, filters);
    } catch (err) {
      console.error('Error in bulk unmap:', err);
      showToast({ type: 'error', message: err.message || 'Failed to bulk unmap products.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleSelectProduct = (product) => {
    const id = activeTab === 'mapped' ? product.mapping_id : getProductId(product);
    if (!id) return;

    const next = new Set(selectedProducts);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedProducts(next);
  };

  const handleSelectAllCurrentPage = (e) => {
    const next = new Set(selectedProducts);
    if (e.target.checked) {
      selectableProductsOnPage.forEach(p => {
        const id = activeTab === 'mapped' ? p.mapping_id : getProductId(p);
        if (id) next.add(id);
      });
    } else {
      selectableProductsOnPage.forEach(p => {
        const id = activeTab === 'mapped' ? p.mapping_id : getProductId(p);
        if (id) next.delete(id);
      });
    }
    setSelectedProducts(next);
  };

  const handleSelectAllAcrossStore = () => {
    const next = new Set();
    allUnmappedProducts.forEach(p => {
      const id = getProductId(p);
      if (id) next.add(id);
    });
    setSelectedProducts(next);
  };

  const handleClearSelection = () => {
    setSelectedProducts(new Set());
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value
    };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setFilterLoading(true);
    
    // Fetch products with new filters
    if (selectedVendorId) {
      fetchProducts(selectedVendorId, newFilters).finally(() => {
        setFilterLoading(false);
      });
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    const clearedFilters = DEFAULT_PRODUCT_FILTERS;
    setFilters(clearedFilters);
    setCurrentPage(1);
    setFilterLoading(true);
    
    if (selectedVendorId) {
      fetchProducts(selectedVendorId, clearedFilters).finally(() => {
        setFilterLoading(false);
      });
    }
  };

  // Check if any filter is active
  const hasActiveFilters = filters.vendor_shopify_status || filters.suspire_shopify_status;

  const handleBulkPush = async () => {
    if (selectedProducts.size === 0) return;
    if (selectedProducts.size > 50) {
      showToast({ type: 'error', message: 'Maximum 50 products can be pushed at once' });
      return;
    }

    try {
      setSubmittingId('bulk-push');
      await executeBulkPush();
    } catch (err) {
      console.error('Error in bulk push:', err);
      showToast({ type: 'error', message: err.message || 'Failed to bulk push products.' });
      setSubmittingId(null);
    }
  };

  const executeBulkPush = async () => {
    try {
      // v2 ingest fetches each product server-side from the vendor store, so
      // only the vendor Shopify product id is sent per product.
      const payloadProducts = [];
      for (const productId of selectedProducts) {
        const product = products.find(p => getProductId(p) === productId);
        if (product) {
          payloadProducts.push({
            vendor_shopify_product_id: Number(productId),
          });
        }
      }

      if (payloadProducts.length === 0) return;

      const response = await bulkPushProductsToCatalog(selectedVendorId, { products: payloadProducts });

      const results = response.results || [];
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.length - successCount;

      if (failedCount > 0) {
        const errorDetails = results
          .filter(r => !r.success)
          .map(r => `${r.title || r.vendor_shopify_product_id}: ${r.error || 'Failed'}`)
          .join(' | ');
        showToast({
          type: 'error',
          message: `${successCount} pushed, ${failedCount} failed. Errors: ${errorDetails}`
        });
      } else {
        showToast({
          type: 'success',
          message: `${successCount} products pushed successfully.`
        });
      }

      await fetchProducts(selectedVendorId, filters);
      setSelectedProducts(new Set());
    } catch (err) {
      console.error('Error in bulk push:', err);
      showToast({ type: 'error', message: err.message || 'Failed to bulk push products.' });
    } finally {
      setSubmittingId(null);
    }
  };

  const isMappedTabContent = useCallback((p) => {
    return p.mapping_status === "mapped";
  }, []);

  const isUnmappedTabContent = useCallback((p) => {
    if (isMappedTabContent(p)) return false;
    return true;
  }, [isMappedTabContent]);

  // Filter products for tabs
  const filteredProducts = useMemo(() => {
    let base = [];
    if (activeTab === 'unmapped') {
      base = products.filter(isUnmappedTabContent);
    } else {
      base = products.filter(isMappedTabContent);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      base = base.filter(p =>
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q))
      );
    }

    if (filters.vendor_shopify_status) {
      const vs = filters.vendor_shopify_status.toLowerCase();
      base = base.filter(p => (p.vendor_shopify_status || '').toLowerCase() === vs);
    }

    if (filters.suspire_shopify_status) {
      const ss = filters.suspire_shopify_status.toLowerCase();
      base = base.filter(p => (p.suspire_shopify_status || '').toLowerCase() === ss);
    }

    return base;
  }, [products, activeTab, searchQuery, filters, isUnmappedTabContent, isMappedTabContent]);

  // Paginated products
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  const selectableProductsOnPage = useMemo(() => {
    if (activeTab === 'unmapped') {
      return paginatedProducts.filter(p => p.mapping_status === 'unmapped');
    } else {
      return paginatedProducts.filter(p => p.mapping_status === 'mapped');
    }
  }, [paginatedProducts, activeTab]);

  const allUnmappedProducts = useMemo(() => {
    return products.filter(p => p.mapping_status === 'unmapped');
  }, [products]);

  const allMappedProducts = useMemo(() => {
    return products.filter(p => p.mapping_status === 'mapped');
  }, [products]);

  const isAllSelectableChecked = useMemo(() => {
    return selectableProductsOnPage.length > 0 && selectableProductsOnPage.every(p => {
      const id = activeTab === 'mapped' ? p.mapping_id : getProductId(p);
      return selectedProducts.has(id);
    });
  }, [selectableProductsOnPage, selectedProducts, activeTab]);

  const someSelectedOnPage = useMemo(() => {
    return selectableProductsOnPage.some(p => {
      const id = activeTab === 'mapped' ? p.mapping_id : getProductId(p);
      return selectedProducts.has(id);
    });
  }, [selectableProductsOnPage, selectedProducts, activeTab]);

  const isIndeterminate = someSelectedOnPage && !isAllSelectableChecked;

  const selectedProductsData = useMemo(() => {
    return products.filter(p => selectedProducts.has(getProductId(p)));
  }, [products, selectedProducts]);

  const deletedInSelection = useMemo(() => {
    return selectedProductsData.filter(p => (p.suspire_shopify_status || '').toUpperCase() === 'DELETED').length;
  }, [selectedProductsData]);

  const selectedVendorData = useMemo(() => {
    if (!selectedVendorId) return null;
    if (config?.is_admin) {
      return vendors.find(v => v.id === selectedVendorId);
    }
    return config;
  }, [selectedVendorId, vendors, config]);

  if (loading) {
    return (
      <div className="vstore-loading-state">
        <div className="spinner"></div>
        <p>Checking your shopify configuration...</p>
      </div>
    );
  }

  if (error) {
    // "Account Not Linked" is the right answer when the config really is
    // missing, and the wrong one when we simply could not reach the server:
    // telling a correctly-linked vendor to contact support about a deploy
    // sends them down a blind alley. An outage keeps its own description and
    // offers a retry; anything else keeps the configuration guidance.
    const classified = typeof error === 'string' ? null : error;
    if (classified && classified.retryable) {
      return (
        <div className="vstore-error-state">
          <DataFallback variant="pane" error={classified} onRetry={() => window.location.reload()} />
        </div>
      );
    }
    return (
      <div className="vstore-error-state">
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <h3>Account Not Linked</h3>
          <p>{typeof error === 'string' ? error : error.message}</p>
          <p className="error-help">Please contact support to link your account to a vendor store.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vstore-container">
      {/* Admin Dropdowns */}
      {config.is_admin && (
        <div className="vstore-admin-header" style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          {/* Synced Vendors Dropdown */}
          <div className="vstore-selector-group" style={{ flex: 1 }}>
            <label>Synced Vendors</label>
            <div className="vstore-searchable-dropdown" ref={dropdownRef}>
              <div className="vstore-search-input-wrapper">
                <input
                  type="text"
                  className="vstore-search-input"
                  placeholder="Search synced vendors..."
                  value={vendorSearch}
                  onChange={(e) => {
                    setVendorSearch(e.target.value);
                    setDropdownOpen(true);
                    if (!e.target.value) {
                      selectVendor(null);
                    }
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  disabled={vendorsLoading}
                />
                {selectedVendorId && (
                  <button
                    className="vstore-search-clear"
                    onClick={() => selectVendor(null)}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
                <span
                  className={`vstore-search-chevron ${dropdownOpen ? 'open' : ''}`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  ▾
                </span>
              </div>
              {dropdownOpen && (
                <ul className="vstore-dropdown-list">
                  {filteredVendors.items.length > 0 ? (
                    <>
                      {filteredVendors.items.map(v => (
                        <li
                          key={v.id}
                          className={`vstore-dropdown-item ${selectedVendorId === v.id ? 'selected' : ''}`}
                          onClick={() => selectVendor(v.id)}
                        >
                          {highlightMatch(getVendorName(v), vendorSearch)}
                        </li>
                      ))}
                      {!filteredVendors.isSearching && vendors.length > filteredVendors.items.length && (
                        <li className="vstore-dropdown-hint">
                          Showing {filteredVendors.items.length} of {vendors.length} — type at least {VENDOR_SEARCH_MIN_CHARS} characters to search
                        </li>
                      )}
                    </>
                  ) : (
                    <li className="vstore-dropdown-empty">No synced vendors found</li>
                  )}
                </ul>
              )}
            </div>
            {vendorsLoading && <span className="small-spinner"></span>}
          </div>

          {/* Unsynced Vendors Dropdown */}
          <div className="vstore-selector-group" style={{ flex: 1 }}>
            <label>Unsynced Vendors</label>
            <div className="vstore-searchable-dropdown" ref={unsyncedDropdownRef}>
              <div className="vstore-search-input-wrapper">
                <input
                  type="text"
                  className="vstore-search-input"
                  placeholder="Search unsynced vendors..."
                  value={unsyncedVendorSearch}
                  onChange={(e) => {
                    setUnsyncedVendorSearch(e.target.value);
                    setUnsyncedDropdownOpen(true);
                  }}
                  onFocus={() => setUnsyncedDropdownOpen(true)}
                  disabled={vendorsLoading}
                />
                <span
                  className={`vstore-search-chevron ${unsyncedDropdownOpen ? 'open' : ''}`}
                  onClick={() => setUnsyncedDropdownOpen(!unsyncedDropdownOpen)}
                >
                  ▾
                </span>
              </div>
              {unsyncedDropdownOpen && (
                <ul className="vstore-dropdown-list">
                  {filteredUnsyncedVendors.items.length > 0 ? (
                    <>
                      {filteredUnsyncedVendors.items.map(v => (
                        <li
                          key={v.id}
                          className="vstore-dropdown-item"
                          onClick={() => selectUnsyncedVendor(v.id)}
                        >
                          {highlightMatch(getVendorName(v), unsyncedVendorSearch)}
                        </li>
                      ))}
                      {!filteredUnsyncedVendors.isSearching && unsyncedVendors.length > filteredUnsyncedVendors.items.length && (
                        <li className="vstore-dropdown-hint">
                          Showing {filteredUnsyncedVendors.items.length} of {unsyncedVendors.length} — type at least {VENDOR_SEARCH_MIN_CHARS} characters to search
                        </li>
                      )}
                    </>
                  ) : (
                    <li className="vstore-dropdown-empty">No unsynced vendors found</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Card & Action Bar */}
      {selectedVendorId && (
        <div className="vstore-status-bar">
          <div className="vstore-status-card">
            <div className="status-item">
              <span className="label">Shopify Domain:</span>
              <span className="value">
                {selectedVendorData?.shopify_domain ||
                  (selectedVendorData?.shopify_shop_name ? `${selectedVendorData.shopify_shop_name}.myshopify.com` : 'N/A')}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Status:</span>
              {selectedVendorData?.has_shopify_connected ? (
                <span className="status-badge connected" style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', marginRight: '6px' }}></span>
                  Connected
                </span>
              ) : (
                <span className="status-badge" style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: '800', padding: '4px 10px', borderRadius: '12px' }}>
                  Not Connected
                </span>
              )}
            </div>
          </div>

          {!selectedVendorData?.has_shopify_connected && (
            <div style={{
              background: '#fefce8',
              border: '1px solid #fef08a',
              color: '#854d0e',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>💡</span> No Shopify store connected. Go to vendor settings to connect.
            </div>
          )}
        </div>
      )}

      {/* Product List Content */}
      {selectedVendorId && (
        <div className="vstore-content">
          <div className="vstore-tabs">
            <button
              className={activeTab === 'unmapped' ? 'active' : ''}
              onClick={() => { setActiveTab('unmapped'); setSelectedProducts(new Set()); }}
            >
              Unmapped <span className="tab-count">({unmappedCount})</span>
            </button>
            <button
              className={activeTab === 'mapped' ? 'active' : ''}
              onClick={() => { setActiveTab('mapped'); setSelectedProducts(new Set()); }}
            >
              Mapped <span className="tab-count">({mappedCount})</span>
            </button>
          </div>
          
          {/* Product count summary header */}
          <div style={{ 
            padding: '12px 16px', 
            background: '#f8fafc', 
            borderRadius: '8px', 
            marginBottom: '16px',
            fontSize: '14px',
            color: '#64748b',
            fontWeight: '500'
          }}>
            {totalCount} products · {mappedCount} mapped · {unmappedCount} unmapped
          </div>
          
          <div className="vstore-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div className="vstore-search-bar" style={{ position: 'relative', width: '360px' }}>
              <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search by product name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>
            
            {/* Filter Dropdowns */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={filters.vendor_shopify_status}
                onChange={(e) => handleFilterChange('vendor_shopify_status', e.target.value)}
                disabled={filterLoading}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  outline: 'none',
                  fontSize: '14px',
                  backgroundColor: filterLoading ? '#f8fafc' : 'white',
                  cursor: filterLoading ? 'not-allowed' : 'pointer',
                  minWidth: '180px',
                  opacity: filterLoading ? 0.6 : 1
                }}
              >
                <option value="">Vendor Shopify Status: All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
                <option value="unlisted">Unlisted</option>
                <option value="deleted">Deleted</option>
              </select>
              
              <select
                value={filters.suspire_shopify_status}
                onChange={(e) => handleFilterChange('suspire_shopify_status', e.target.value)}
                disabled={filterLoading}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  outline: 'none',
                  fontSize: '14px',
                  backgroundColor: filterLoading ? '#f8fafc' : 'white',
                  cursor: filterLoading ? 'not-allowed' : 'pointer',
                  minWidth: '180px',
                  opacity: filterLoading ? 0.6 : 1
                }}
              >
                <option value="">Suspire Shopify Status: All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
                <option value="unlisted">Unlisted</option>
                <option value="deleted">Deleted</option>
              </select>
              
              {filterLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#166534',
                  fontWeight: '500'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #bbf7d0',
                    borderTop: '2px solid #166534',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}></div>
                  <span>Applying filters...</span>
                </div>
              )}
              
              {hasActiveFilters && !filterLoading && (
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: 'white',
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.borderColor = '#e2e8f0';
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {activeTab === 'unmapped' && selectedProducts.size > 0 && (
            <div className="selectionBanner">
              <div className="selectionBannerContent">
                <span className="selectionCount">
                  {selectedProducts.size} {selectedProducts.size === 1 ? 'Product' : 'Products'} Selected
                  {deletedInSelection > 0 && (
                    <span style={{ color: '#dc2626', marginLeft: '6px', fontWeight: 'bold' }}>
                      ({deletedInSelection} previously deleted from Suspire)
                    </span>
                  )}
                </span>
                <button
                  className="selectionBannerButton"
                  onClick={handleClearSelection}
                >
                  Unselect Products
                </button>
                {selectedProducts.size < allUnmappedProducts.length && (
                  <>
                    <span className="selectionBannerDivider">/</span>
                    <button
                      className="selectionBannerButton"
                      onClick={handleSelectAllAcrossStore}
                    >
                      Select All {allUnmappedProducts.length} Products
                    </button>
                  </>
                )}
              </div>
              {selectedProducts.size >= 1 && (
                <button
                  onClick={handleBulkPush}
                  disabled={submittingId === 'bulk-push'}
                  className="bulk-push-btn"
                >
                  {submittingId === 'bulk-push' ? (
                    <span className="btn-loading">
                      <span className="spinner-small"></span>
                      Pushing {selectedProducts.size} products...
                    </span>
                  ) : `Bulk Push to Map (${selectedProducts.size})`}
                </button>
              )}
            </div>
          )}

          {activeTab === 'mapped' && selectedProducts.size > 0 && (
            <div className="selectionBanner unmap">
              <div className="selectionBannerContent">
                <span className="selectionCount">
                  {selectedProducts.size} {selectedProducts.size === 1 ? 'Product' : 'Products'} Selected for Unmapping
                </span>
                <button
                  className="selectionBannerButton"
                  onClick={handleClearSelection}
                >
                  Unselect Products
                </button>
              </div>
              <button
                onClick={handleBulkUnmap}
                disabled={submittingId === 'bulk-unmap'}
                className="bulk-unmap-btn"
              >
                {submittingId === 'bulk-unmap' ? (
                  <span className="btn-loading">
                    <span className="spinner-small"></span>
                    Unmapping {selectedProducts.size} products...
                  </span>
                ) : `Bulk Unmap (${selectedProducts.size} Products)`}
              </button>
            </div>
          )}

          <div className="vstore-table-container">
            {productsLoading && (
              <div className="table-overlay">
                <div className="spinner"></div>
              </div>
            )}

            <table className="vstore-table">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isAllSelectableChecked}
                      ref={input => { if (input) input.indeterminate = isIndeterminate; }}
                      onChange={handleSelectAllCurrentPage}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </th>
                  <th>Image</th>
                  <th>Product ID</th>
                  <th>Name</th>
                  <th>SKU</th>
                  <th>Vendor Shopify</th>
                  <th>Suspire Status</th>
                  <th>Mapping</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.length > 0 ? (
                  paginatedProducts.map((product, index) => {
                    const productId = getProductId(product);
                    // Selectable in unmapped tab: unmapped products (includes deleted/re-push candidates)
                    const isSelectable = activeTab === 'unmapped' && product.mapping_status === 'unmapped';

                    return (
                      <tr key={productId || index} className="product-row">
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedProducts.has(activeTab === 'mapped' ? product.mapping_id : productId)}
                            onChange={() => handleSelectProduct(product)}
                          />
                        </td>
                        <td>
                          <div className="product-image-cell">
                            {product.image?.src || product.image ? (
                              <img src={product.image?.src || product.image} alt={product.title} />
                            ) : (
                              <div className="no-image-box">No Image</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <code
                            className="clickable-id"
                            onClick={() => navigate(`/suspire-terminal/vendor-store/product/${productId}?vendor_id=${selectedVendorId}`)}
                            style={!productId ? { color: '#dc2626', background: '#fee2e2' } : {}}
                          >
                            {productId || 'No ID'}
                          </code>
                        </td>
                        <td>
                          <div
                            className="product-info"
                            onClick={() => navigate(`/suspire-terminal/vendor-store/product/${productId}?vendor_id=${selectedVendorId}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span className="product-title clickable">{product.title}</span>
                          </div>
                        </td>
                        <td>
                          <span className="product-sku">{product.sku || '-'}</span>
                        </td>
                        <td>
                          <StatusBadge status={product.vendor_shopify_status} />
                        </td>
                        <td>
                          <StatusBadge status={product.suspire_shopify_status} showCallout={activeTab === 'unmapped'} />
                        </td>
                        <td>
                          {product.mapping_status === 'mapped' ? (
                            <span className="badge mapped">Mapped</span>
                          ) : (
                            <span className="badge unmapped">Unmapped</span>
                          )}
                        </td>
                        <td>
                          <div className="vstore-table-actions">
                            {/* Push to Map — mapping_status === 'unmapped' */}
                            {activeTab === 'unmapped' && product.mapping_status === 'unmapped' && (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                                {/* Still linked, just unmapped: re-mapping is a flag flip that
                                    reuses the stored variant matches, so offer it directly
                                    instead of sending the user back through the link modal. */}
                                {product.is_linked ? (
                                  <button
                                    className="remap-btn tooltip"
                                    title="Re-map — restore vendor sync using the existing link"
                                    onClick={() => handleRemap(product)}
                                    disabled={submittingId === productId}
                                  >
                                    {submittingId === productId ? (
                                      <span className="spinner-small" style={{ margin: 0 }}></span>
                                    ) : 'Re-map'}
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      className="push-icon-btn tooltip"
                                      title="Push to Merchant Store"
                                      onClick={() => handlePushToCatalog(product)}
                                      disabled={submittingId === productId}
                                    >
                                      {submittingId === productId ? (
                                        <span className="spinner-small" style={{ margin: 0 }}></span>
                                      ) : <FaPlus />}
                                    </button>
                                    <button
                                      className="link-icon-btn tooltip"
                                      title="Link Existing Product"
                                      onClick={() => handleOpenLinkPopup(product)}
                                      disabled={submittingId === productId}
                                    >
                                      <FaLink />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Pending state - admin only action */}
                            {activeTab === 'unmapped' && product.mapping_status === 'unmapped' && !product.mapping_id && product.internal_product_status === 'pending_approval' && (
                              config.is_admin ? (
                                <button
                                  className="push-btn approve"
                                  onClick={() => handleApprove(product)}
                                  disabled={submittingId === product.internal_product_id}
                                >
                                  {submittingId === product.internal_product_id ? '...' : 'Approve'}
                                </button>
                              ) : (
                                <span className="no-action">-</span>
                              )
                            )}

                            {/* Edit Action - ONLY in mapped tab */}
                            {activeTab === 'mapped' && product.mapping_id && (
                              <button
                                className="edit-icon-btn"
                                onClick={() => handleStartEdit(product)}
                                title="Edit Mapping"
                              >
                                ✎
                              </button>
                            )}

                            {/* Unmap button - only in mapped tab */}
                            {activeTab === 'mapped' && (
                              <button
                                className="unmap-btn"
                                onClick={() => handleUnmap(product)}
                                disabled={submittingId === productId}
                              >
                                Unmap
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={activeTab === 'unmapped' ? "10" : "9"} className="empty-row">
                      {!productsLoading && (
                        <div className="empty-state">
                          {products.length === 0 ? (
                            "No products found in this vendor's Shopify store."
                          ) : activeTab === 'unmapped' ? (
                            searchQuery ? "No unmapped products match your search." : "All products are already mapped."
                          ) : (
                            searchQuery ? "No mapped products match your search." : "No products have been mapped yet."
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination UI & Stats moved here */}
          <div className="vstore-footer-actions">
            <div className="vstore-pagination-summary">
              Showing {Math.min(filteredProducts.length, (currentPage - 1) * pageSize + 1)} to {Math.min(filteredProducts.length, currentPage * pageSize)} of {filteredProducts.length} Products
            </div>
            {totalPages > 1 && (
              <div className="vstore-pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(prev => prev - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage(prev => prev + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!selectedVendorId && config.is_admin && !loading && (
        <div className="vstore-placeholder">
          <div className="placeholder-content">
            <div className="placeholder-icon">📦</div>
            <h3>Vendor Store Manager</h3>
            <p>Select a vendor from the dropdown above to manage their Shopify store products.</p>
          </div>
        </div>
      )}

      {/* Full-Screen Edit Modal */}
      {isEditing && (
        <div className="vstore-modal-overlay fullscreen">
          <div className="vstore-modal fullscreen">
            <div className="vstore-modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, padding: '24px 40px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>Edit Product</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  ID: <code style={{ fontSize: '0.75rem' }}>{editingProduct?.mapping_id}</code>
                </p>
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <button className="vstore-back-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                <button
                  className="sync-btn"
                  onClick={handleUpdateMapping}
                  disabled={submittingId !== null}
                >
                  {submittingId ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div className="vstore-modal-body" style={{ padding: '40px' }}>
              {modalError && (
                <div className="modal-error-banner">
                  {modalError}
                </div>
              )}

              <div className="edit-layout" style={{ gridTemplateColumns: '1fr', display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {/* Product Data */}
                <div className="edit-section">
                  <div className="edit-section-title">
                    <span>📦</span> Product Details
                  </div>
                  <div className="edit-card">
                    <div className="form-group">
                      <label>Product Title</label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        required
                      />
                    </div>

                    {config.is_admin && (
                      <div className="form-group">
                        <label>Store Status</label>
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="DRAFT">Draft</option>
                          <option value="ARCHIVED">Archived</option>
                        </select>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Description (Rich Text / HTML)</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        placeholder="Describe your product..."
                        style={{ minHeight: '180px' }}
                      />
                    </div>
                  </div>
                </div>


                {/* Variants - Full Width Below */}
                <div className="edit-section">
                  <div className="edit-section-title">
                    <span>✨</span> Variant Options & Inventory
                  </div>
                  <div className="edit-card">
                    {editForm.variantsData.length > 0 ? (
                      editForm.variantsData.map((v, idx) => (
                        <div key={idx} className="variant-edit-row">
                          <span className="variant-name-label">{v.title}</span>
                          <div className="variant-fields-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                            <div className="form-group">
                              <label>SKU</label>
                              <input
                                type="text"
                                value={v.sku}
                                placeholder="Variant SKU"
                                onChange={(e) => {
                                  const next = [...editForm.variantsData];
                                  next[idx].sku = e.target.value;
                                  setEditForm({ ...editForm, variantsData: next });
                                }}
                              />
                            </div>
                            <div className="form-group">
                              <label>Price</label>
                              <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '14px', top: '12px', color: '#94a3b8', pointerEvents: 'none' }}>₹</span>
                                <input
                                  type="number"
                                  style={{ paddingLeft: '32px' }}
                                  value={v.price}
                                  onChange={(e) => {
                                    const next = [...editForm.variantsData];
                                    next[idx].price = e.target.value;
                                    setEditForm({ ...editForm, variantsData: next });
                                  }}
                                />
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Compare Price</label>
                              <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '14px', top: '12px', color: '#94a3b8', pointerEvents: 'none' }}>₹</span>
                                <input
                                  type="number"
                                  style={{ paddingLeft: '32px' }}
                                  value={v.compare_price}
                                  placeholder="Clear for no discount"
                                  onChange={(e) => {
                                    const next = [...editForm.variantsData];
                                    next[idx].compare_price = e.target.value;
                                    setEditForm({ ...editForm, variantsData: next });
                                  }}
                                />
                              </div>
                            </div>
                            <div className="form-group">
                              <label>HS Code</label>
                              <input
                                type="text"
                                maxLength="20"
                                value={v.hs_code}
                                placeholder="e.g. 950611"
                                onChange={(e) => {
                                  const next = [...editForm.variantsData];
                                  next[idx].hs_code = e.target.value;
                                  setEditForm({ ...editForm, variantsData: next });
                                }}
                              />
                            </div>
                            {config.is_admin && (
                              <>
                                <div className="form-group">
                                  <label>Length (cm)</label>
                                  <input
                                    type="number" step="0.1"
                                    value={v.length_cm}
                                    placeholder="e.g. 30"
                                    onChange={(e) => {
                                      const next = [...editForm.variantsData];
                                      next[idx].length_cm = e.target.value;
                                      setEditForm({ ...editForm, variantsData: next });
                                    }}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Width (cm)</label>
                                  <input
                                    type="number" step="0.1"
                                    value={v.width_cm}
                                    placeholder="e.g. 20"
                                    onChange={(e) => {
                                      const next = [...editForm.variantsData];
                                      next[idx].width_cm = e.target.value;
                                      setEditForm({ ...editForm, variantsData: next });
                                    }}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Height (cm)</label>
                                  <input
                                    type="number" step="0.1"
                                    value={v.height_cm}
                                    placeholder="e.g. 10"
                                    onChange={(e) => {
                                      const next = [...editForm.variantsData];
                                      next[idx].height_cm = e.target.value;
                                      setEditForm({ ...editForm, variantsData: next });
                                    }}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Commission (%)</label>
                                  <input
                                    type="number" step="0.01" min="0" max="100"
                                    value={v.commission_pct}
                                    placeholder="e.g. 15"
                                    onChange={(e) => {
                                      const next = [...editForm.variantsData];
                                      next[idx].commission_pct = e.target.value;
                                      setEditForm({ ...editForm, variantsData: next });
                                    }}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>GST Rate (%)</label>
                                  <input
                                    type="number" step="0.01" min="0" max="100"
                                    value={v.gst_rate}
                                    placeholder="e.g. 18"
                                    onChange={(e) => {
                                      const next = [...editForm.variantsData];
                                      next[idx].gst_rate = e.target.value;
                                      setEditForm({ ...editForm, variantsData: next });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                        No variant options identified for this product.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Product Modal */}
      {linkModalProduct && (
        <div className="vstore-modal-overlay">
          <div className="vstore-modal link-modal" style={{ height: 'min(750px, 90vh)', maxWidth: '900px' }}>
            <div className="vstore-modal-header" style={{ paddingBottom: '16px', borderBottom: 'none' }}>
              <h3 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Add/Edit Mapping</h3>
              <button 
                className="vstore-modal-close" 
                onClick={handleCloseLinkPopup}
              >
                ✕
              </button>
            </div>

            <div className="gold-divider" />

            <div className="link-subtext">
              Select the <u>Unmapped Product</u> in your Merchant's store to which you want to Map this product.
            </div>

            {/* Sticky Dropdown Area */}
            <div style={{ padding: '0 24px 20px 24px', zIndex: 110 }}>
              <div className="vstore-searchable-dropdown" ref={merchantDropdownRef} style={{ width: '100%' }}>
                <label className="detail-label" style={{ display: 'block', marginBottom: '8px' }}>Choose Merchant Product</label>
                <div className="vstore-search-input-wrapper">
                  <input
                    type="text"
                    className="vstore-search-input"
                    placeholder="Search Suspire products by name..."
                    value={merchantSearchQuery}
                    onChange={(e) => handleMerchantSearchChange(e.target.value)}
                    onFocus={() => setMerchantDropdownOpen(true)}
                  />
                  <span className={`vstore-search-chevron ${merchantDropdownOpen ? 'open' : ''}`}>▾</span>
                </div>
                {merchantDropdownOpen && (
                  <ul className="vstore-dropdown-list" style={{ width: '100%', maxHeight: '250px' }}>
                    {merchantProductsLoading ? (
                      <li className="vstore-dropdown-empty">Searching...</li>
                    ) : merchantProducts.length > 0 ? (
                      merchantProducts.map(p => (
                        <li
                          key={p.id || p.internal_product_id}
                          className={`vstore-dropdown-item ${selectedMerchantProduct?.id === (p.id || p.internal_product_id) ? 'selected' : ''}`}
                          onClick={() => selectMerchantProduct(p)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>{p.title}</span>
                            {p.is_mapped && <span className="mapped-badge">(already mapped)</span>}
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="vstore-dropdown-empty">No products found</li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            <div className="vstore-modal-body">
              {modalError && (
                <div className="modal-error-banner">
                  {modalError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* Left Card - Vendor Shop Product */}
                <div className="link-modal-card">
                  <div className="link-card-header">
                    <span className="link-card-title">Vendor Shop Product</span>
                    <div className="link-card-image-small">
                      {(linkModalProduct.image?.src || linkModalProduct.image) ? (
                        <img src={linkModalProduct.image?.src || linkModalProduct.image} alt="Vendor" />
                      ) : <span style={{ fontSize: '10px', color: '#cbd5e1' }}>No Image</span>}
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="label">Product Id:</span>
                    <span className="value">{getProductId(linkModalProduct)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Product Title:</span>
                    <span className="value" style={{ fontWeight: '600' }}>{linkModalProduct.title}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Tags:</span>
                    <span className="value">{formatTags(linkModalProduct.tags)}</span>
                  </div>
                  
                  <div className="detail-variants" style={{ marginTop: '8px' }}>
                    <h4 style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '8px', color: '#64748b' }}>Variants</h4>
                    <table className="variants-table" style={{ border: '1px solid #f1f5f9' }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th style={{ fontSize: '10px', padding: '6px' }}>Title</th>
                          <th style={{ fontSize: '10px', padding: '6px' }}>SKU</th>
                          <th style={{ fontSize: '10px', padding: '6px' }}>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(linkModalProduct.variants || []).map((v, i) => (
                          <tr key={i}>
                            <td style={{ fontSize: '11px', padding: '6px' }}>{v.title}</td>
                            <td style={{ fontSize: '11px', padding: '6px' }}>{v.sku || '-'}</td>
                            <td style={{ fontSize: '11px', padding: '6px' }}>{v.inventory_quantity ?? v.quantity ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right Card - Merchant Shop Product */}
                <div className="link-modal-card">
                  <div className="link-card-header">
                    <span className="link-card-title">Merchant Shop Product</span>
                    {selectedMerchantProduct && (
                      <div className="link-card-image-small">
                        {(selectedMerchantProduct.image?.src || selectedMerchantProduct.image) ? (
                          <img src={selectedMerchantProduct.image?.src || selectedMerchantProduct.image} alt="Merchant" />
                        ) : <span style={{ fontSize: '10px', color: '#cbd5e1' }}>No Image</span>}
                      </div>
                    )}
                  </div>
                  
                  {selectedMerchantProduct ? (
                    <>
                      <div className="detail-row">
                        <span className="label">Product Id:</span>
                        <span className="value">{selectedMerchantProduct.shopify_id || selectedMerchantProduct.id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Product Title:</span>
                        <span className="value" style={{ fontWeight: '600' }}>{selectedMerchantProduct.title}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Tags:</span>
                        <span className="value">{formatTags(selectedMerchantProduct.meta?.shopify_tags || selectedMerchantProduct.tags)}</span>
                      </div>
                      
                      <div className="detail-variants" style={{ marginTop: '8px' }}>
                        <h4 style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '8px', color: '#64748b' }}>Variants</h4>
                        <table className="variants-table" style={{ border: '1px solid #f1f5f9' }}>
                          <thead style={{ background: '#f8fafc' }}>
                            <tr>
                              <th style={{ fontSize: '9px', padding: '6px' }}>Title</th>
                              <th style={{ fontSize: '9px', padding: '6px' }}>SKU</th>
                              <th style={{ fontSize: '9px', padding: '6px' }}>Qty</th>
                              <th style={{ fontSize: '9px', padding: '6px' }}>Vendor Variant Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedMerchantProduct.variants || []).map((v, i) => {
                              const merchantVarId = String(v.id || v.internal_variant_id);
                              
                              // Exclusivity: find which vendor variants are used elsewhere
                              const usedVendorIds = Object.entries(linkVariantMappings)
                                .filter(([mId, vId]) => mId !== merchantVarId && vId !== '')
                                .map(([mId, vId]) => String(vId));

                              return (
                                <tr key={i}>
                                  <td style={{ fontSize: '10px', padding: '4px 6px' }}>{v.variant_name || v.title}</td>
                                  <td style={{ fontSize: '10px', padding: '4px 6px' }}>{v.sku || '-'}</td>
                                  <td style={{ fontSize: '10px', padding: '4px 6px' }}>{v.quantity ?? v.inventory_quantity ?? '-'}</td>
                                  <td style={{ padding: '4px 6px' }}>
                                    <select 
                                      className="vstore-select-mini"
                                      value={linkVariantMappings[merchantVarId] || ''}
                                      onChange={(e) => setLinkVariantMappings({
                                        ...linkVariantMappings,
                                        [merchantVarId]: e.target.value
                                      })}
                                      style={{ padding: '2px', fontSize: '9px', height: '24px', width: '100%' }}
                                    >
                                      <option value="">-- No Match --</option>
                                      {(linkModalProduct.variants || []).map(vv => {
                                        const vvId = String(vv.vendor_shopify_variant_id);
                                        if (usedVendorIds.includes(vvId)) return null;
                                        return (
                                          <option key={vvId} value={vvId}>
                                            {vv.title} {vv.sku ? `(${vv.sku})` : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #e2e8f0', borderRadius: '8px', color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                      <div style={{ fontSize: '24px', marginBottom: '12px', opacity: 0.5 }}>🔍</div>
                      Please select a merchant product from the dropdown above to view details
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="map-btn-centered">
               <button 
                  className="map-product-btn" 
                  onClick={handlePushToMap}
                  disabled={submittingId !== null || !selectedMerchantProduct}
               >
                 {submittingId === `link_${getProductId(linkModalProduct)}` ? (
                   <span className="btn-loading">
                     <span className="spinner-small"></span>
                     Mapping...
                   </span>
                 ) : 'Link Product'}
               </button>
            </div>
          </div>
        </div>
      )}


      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="vconfirm-modal-overlay">
          <div className="vconfirm-modal">
            <div className="vconfirm-title">{confirmModal.props.title}</div>
            <div className="vconfirm-message">{confirmModal.props.message}</div>

            <div className="vconfirm-summary-grid">
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.title ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.title ? "✓" : "✗"}
                </span>
                Title
              </div>
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.description ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.description ? "✓" : "✗"}
                </span>
                Description
              </div>
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.sku ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.sku ? "✓" : "✗"}
                </span>
                SKU
              </div>
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.price ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.price ? "✓" : "✗"}
                </span>
                Price
              </div>
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.variants ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.variants ? "✓" : "✗"}
                </span>
                Variants
              </div>
              <div className="vconfirm-summary-item">
                <span className={confirmModal.props.filters.inventory ? "vconfirm-check" : "vconfirm-cross"}>
                  {confirmModal.props.filters.inventory ? "✓" : "✗"}
                </span>
                Inventory
              </div>
            </div>

            <div className="vconfirm-footer">
              <button
                className="vconfirm-btn-cancel"
                onClick={confirmModal.props.onClose}
              >
                Cancel
              </button>
              <button
                className="vconfirm-btn-proceed"
                onClick={confirmModal.props.onConfirm}
              >
                OK, Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorStoreManager;
